import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import PopupBanner from "../../components/PopupBanner";
import { ReactComponent as InfoIcon } from '../../image/info.svg';
import { ReactComponent as WarningIcon } from '../../image/warning.svg';
import { ReactComponent as CriticalIcon } from '../../image/critical.svg';
import { useTranslation } from 'react-i18next';
import { ErrorHandler } from "../../utils/errorHandlers";

const BannerConfiguration = ({ isPanelOpen , togglePanel, editingBannerId,getAllBanners,setMessageForBanner }) => {
  const id = editingBannerId;
  const [isEnabled, setIsEnabled] = useState(true);
  const [severity, setSeverity] = useState("warning");
  const { t } = useTranslation();
  
  const { titleText, BannerConfiguration: configTitle, EnableDashboardBanner, toggleDesc } = t('BannerConfiguration');
  
  const severityIcons = {
    info: <InfoIcon className="w-7 h-7 icon-blue" />,
    warning: <WarningIcon className="w-7 h-7 icon-orange" />,
    critical: <CriticalIcon className="w-7 h-7 icon-red" />,
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const title = watch("title", "");
  const message = watch("message", "");
  const startDate = watch("startDate");
  const user = JSON.parse(localStorage.getItem('userData'));
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    .toLocaleDateString('sv-SE') + "T00:00";

  const onSubmit = (data) => {
    setLoading(true);
    const payload = {
      isEnabled,
      severityLevel: severity.toUpperCase(),
      title: data.title,
      message: data.message,
      startDateTime: data.startDate,
      endDateTime: data.endDate,
      targetRole: [],
      ...(id
        ? { updatedBy: Number(user?.hftOracleId) }
        : { createdBy: Number(user?.hftOracleId) }
      )
    };

    const request = id
      ? axiosInstance.post(`/api/admin/updateBanner/${id}`, payload)
      : axiosInstance.post('/api/admin/createBanner', payload);

    request
      .then((res) => {
        setMessageForBanner(res?.data?.message || "Operation successful!");
        setLoading(false);
       
        setTimeout(() => {
          if (togglePanel) togglePanel(); 
          getAllBanners()
        }, 1000);
      })
      .catch((err) => {
        setLoading(false);
        const message = ErrorHandler(err);
        setMessageForBanner(
          <>
            <span style={{ color: 'red', fontWeight: 'bold' }}>{message}</span>
          </>
        );
        console.error("API Error:", err);
      });
  };  

  useEffect(() => {
    if (id) {
      getBannerById();
    }
  }, [id]);

  const getBannerById = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/admin/getBanner/${id}`
      );
      const data = res?.data?.data;

      setIsEnabled(data?.isEnabled);
      setSeverity(data?.severityLevel?.toLowerCase());

      reset({
        title: data?.title || "",
        message: data?.message || "",
        startDate: data?.startDatetime
          ? data.startDatetime.slice(0, 16)
          : "",
        endDate: data?.endDatetime
          ? data.endDatetime.slice(0, 16)
          : "",
      });
    } catch (err) {
      console.log(err);
      const message = ErrorHandler(err);
      setMessageForBanner(
        <>
          <span style={{ color: 'red', fontWeight: 'bold' }}>{message}</span>
        </>
      );
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    if (!isEnabled) {
      // setSeverity('');
      clearErrors();
    }
  }, [isEnabled]);

  if (!isPanelOpen) return null;

  return (
    <>
      {/* Overlay Background */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40 cursor-pointer transition-opacity"
        onClick={togglePanel}
      />

      {/* Slide-out Side Panel */}
      <aside className="fixed top-0 right-0 w-[600px] h-full bg-white border border-gray-200 shadow-xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out font-sans">
        
        {/* Close Button Cross Symbol */}
        <button
          type="button"
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 text-xl font-light z-50 focus:outline-none"
          onClick={togglePanel}
          aria-label="Close panel"
        >
          ✕
        </button>

        <div className="p-6 sm:p-8">
          {loading ? (
            <div className='flex justify-center items-center h-[50vh]'>
              <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="banner-config-container !p-0">
                <div className="tab-content active">
                  <div className="editor-layout">
                    <div className="panel">
                      <div className="panel-title mb-6 text-lg font-bold tracking-normal text-slate-900 border-none uppercase">
                        {configTitle || "Banner Configuration"}
                      </div>

                      {/* Enable Toggle */}
                      <div className={`toggle-row ${isEnabled ? "on" : "off"} mb-6`}>
                        <div className="toggle-info">
                          <div className="toggle-title font-semibold text-gray-900">{EnableDashboardBanner || "Enable Dashboard Banner"}</div>
                          <div className="toggle-desc text-xs text-gray-500">
                            {toggleDesc || "Show notification to all vendor users on login"}
                          </div>
                        </div>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e) => setIsEnabled(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                      </div>

                      {/* Severity Selection Selection Pills */}
                      <div className="severity-group mb-6">
                        {["info", "warning", "critical"].map((lvl) => (
                          <div
                            key={lvl}
                            className={`severity-option ${lvl} 
                              ${severity === lvl ? "selected" : ""} 
                              ${!isEnabled ? "disabled-field" : ""}
                            `}
                            onClick={() => {
                              if (!isEnabled) return;
                              setSeverity(lvl);
                            }}
                          >
                            {severityIcons[lvl]}
                            <span className="ml-1">{lvl.charAt(0).toUpperCase() + lvl.slice(1)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Title input field */}
                      <div className="form-group mb-4">
                        <label className="form-label font-semibold text-gray-700 block mb-1">
                          {titleText || "Title"} <span className="required text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-input w-full p-2.5 border rounded focus:outline-none focus:border-blue-500
                            ${errors.title ? "error-border border-red-500" : "border-gray-300"} 
                            ${!isEnabled ? "disabled-field bg-gray-50" : ""}
                          `}
                          readOnly={!isEnabled}
                          {...register("title", {
                            required: "Title is required",
                            maxLength: {
                              value: 60,
                              message: "Title cannot exceed 60 characters",
                            },
                          })}
                        />
                        <div className="flex justify-between items-center mt-1 text-xs text-gray-400">
                          <span className="error-text text-red-500">{errors.title?.message}</span>
                          <div className="char-count">{title.length} / 60</div>
                        </div>
                      </div>

                      {/* Split dates layout wrapper */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="form-group">
                          <label className="form-label font-semibold text-gray-700 block mb-1">
                            Start Date & Time <span className="required text-red-500">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            className={`form-input w-full p-2.5 border rounded focus:outline-none focus:border-blue-500
                              ${errors.startDate ? "error-border border-red-500" : "border-gray-300"} 
                              ${!isEnabled ? "disabled-field bg-gray-50" : ""}
                            `}
                            readOnly={!isEnabled}
                            onClick={(e) => e.target.showPicker?.()}
                            {...register("startDate", {
                              required: "Start date is required",
                            })}
                            min={todayMidnight}                          />
                          <span className="error-text text-xs text-red-500 block mt-1">
                            {errors.startDate?.message}
                          </span>
                        </div>

                        <div className="form-group">
                          <label className="form-label font-semibold text-gray-700 block mb-1">
                            End Date & Time <span className="required text-red-500">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            className={`form-input w-full p-2.5 border rounded focus:outline-none focus:border-blue-500
                              ${errors.endDate ? "error-border border-red-500" : "border-gray-300"} 
                              ${!isEnabled ? "disabled-field bg-gray-50" : ""}
                            `}
                            readOnly={!isEnabled}
                            min={startDate ? startDate : new Date().toISOString().slice(0, 16)}
                            onClick={(e) => e.target.showPicker?.()}
                            {...register("endDate", {
                              required: "End date is required",
                              validate: (value) => {
                                if (!startDate || !value) return true;
                                return new Date(value) > new Date(startDate) || "End time must be after start time";
                              },
                            })}
                          />
                          <span className="error-text text-xs text-red-500 block mt-1">
                            {errors.endDate?.message}
                          </span>
                        </div>
                      </div>

                      {/* Message input field */}
                      <div className="form-group mb-6">
                        <label className="form-label font-semibold text-gray-700 block mb-1">
                          Message <span className="required text-red-500">*</span>
                        </label>
                        <textarea
                          className={`form-input w-full p-2.5 border rounded h-28 resize-none focus:outline-none focus:border-blue-500
                            ${errors.message ? "error-border border-red-500" : "border-gray-300"} 
                            ${!isEnabled ? "disabled-field bg-gray-50" : ""}
                          `}
                          maxLength={240}
                          readOnly={!isEnabled}
                          {...register("message", {
                            required: "Message is required",
                            maxLength: {
                              value: 240,
                              message: "Message cannot exceed 240 characters",
                            },
                          })}
                        />
                        <div className="flex justify-between items-center mt-1 text-xs text-gray-400">
                          <span className="error-text text-red-500">{errors.message?.message}</span>
                          <div className="char-count">{message.length} / 240</div>
                        </div>
                      </div>

                      <div className="form-group">
                    <label className="form-label">Banner Preview</label>
                    <div className="preview-wrap">
                      {true ? (
                        <div className={`preview-banner ${severity}`}>
                          <div className="preview-icon">
                            {severityIcons[severity]}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="preview-title">
                              {title || "No Title"}
                            </div>
                            <div className="preview-msg whitespace-pre-wrap">
                              {message || "No Message Content"}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="preview-placeholder">
                          Banner is currently disabled
                        </div>
                      )}
                    </div>
                  </div>
                      {/* Actions Footer Strip */}
                      <div className="btn-group flex justify-end gap-3 pt-4">
                        <button
                          type="button"
                          className="px-5 py-2 rounded font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                          onClick={togglePanel}
                        >
                          Cancel
                        </button>
                        <button
                         className="px-5 py-2 rounded font-medium text-white transition-colors bg-blue-800 hover:bg-blue-900 disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
                          type="submit"
                          disabled={!id && !isEnabled}
                        >
                          {id ? 'Update Banner' : 'Publish Banner'}
                        </button>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </aside>

      
    </>
  );
};

export default BannerConfiguration;